import { useWindowHeight, useWindowWidth } from '@react-hook/window-size';
import {
	Breakpoints,
	Container,
	ContainerPadding,
	Layout,
	styled,
	Text,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { AnteriorCTA, Legales } from 'src/components';
import { COLOR_TOKEN } from 'src/components/color-token';
import { useTrackingManager } from 'src/feature-app/hooks/use-tracking-manager';
import { PreviousStep } from 'src/feature-app/Screen';
import { TRANSITION_TIME } from 'src/globals';
import { Checkmark } from 'src/icons-core-imports';
import { OneFormState } from 'src/types';
import { keyframes } from 'styled-components';
import { useOneFormContext } from '../OneForm';

const checkmarkAnimation = keyframes`
	0% {
		transform: rotate(-90deg);
	}
	60% {
		transform: rotate(45deg);
	}
	100% {
		transform: rotate(0deg);
	}
`;

const AnimatedCheckmark = styled.div`
	animation: ${checkmarkAnimation} ${TRANSITION_TIME.FAST}ms ease-in;
	svg {
		color: ${COLOR_TOKEN.main.content.primary};
	}
`;

const FullScreenWrapper = styled.div<{
	isTrigger: boolean;
}>`
	padding-top: 16px;
	padding-bottom: 160px;
	width: 100%;
	min-height: 100vh;
	height: 100%;
	position: relative;
	display: flex;
	flex-wrap: wrap;
	overflow: ${props => (props.isTrigger ? 'hidden' : 'visible')};
`;

const ContentWrapper = styled.div<{
	alingItemsCenter?: boolean;
}>`
	width: 100%;
	height: 100%;
	overflow: visible;
	align-self: ${props => (props.alingItemsCenter ? 'center' : 'auto')};
	padding: 10px 0;
`;

const PreviousStepAnimationWrapper = styled.div<{
	moveForward: boolean;
	showPreviousStep: boolean;
}>`
	transition: all ${TRANSITION_TIME.REGULAR}ms ease-in-out;

	&.title-animation-enter {
		opacity: 0;
		transform: translateY(0);
	}
	&.title-animation-enter-active,
	&.title-animation-enter-done {
		opacity: 1;
		transform: translateY(0);
	}

	&.prevstep-animation-exit {
		opacity: 1;
		transform: translateY(0);
	}
	&.prevstep-animation-exit-active,
	&.prevstep-animation-exit-done {
		opacity: 0;
		transform: translateY(${props => (props.moveForward ? '0' : '25vh')});
	}

	&.no-animation {
		opacity: 1;
	}
`;

const TitleAnimationWrapper = styled.div<{
	moveForward: boolean;
	hasStickyTitle?: boolean;
}>`
	width: 100%;

	transition: all ${TRANSITION_TIME.REGULAR}ms ease-in-out;
	opacity: 0;
	transform: translateY(
		${props =>
			props.hasStickyTitle ? '0' : props.moveForward ? '2vh' : '-2vh'}
	);

	&.title-animation-enter {
		opacity: 0;
		transform: translateY(
			${props =>
				props.hasStickyTitle ? '0' : props.moveForward ? '2vh' : '-2vh'}
		);
	}
	&.title-animation-enter-active,
	&.title-animation-enter-done {
		opacity: 1;
		transform: translateY(0);
	}

	&.title-animation-exit {
		opacity: 1;
		transform: translateY(0);
	}
	&.title-animation-exit-active,
	&.title-animation-exit-done {
		opacity: 0;
		transform: translateY(
			${props =>
				props.hasStickyTitle
					? '0'
					: props.moveForward
					? '-35vh'
					: '25vh'}
		);
	}

	&.no-animation {
		opacity: 1;
	}
`;

const FieldsAnimationWrapper = styled.div<{
	moveForward: boolean;
	hasStickyTitle?: boolean;
}>`
	transition: all ${TRANSITION_TIME.REGULAR}ms ease-in-out;

	opacity: 0;
	transform: translateY(
		${props =>
			props.hasStickyTitle ? '0' : props.moveForward ? '2vh' : '-2vh'}
	);

	&.fields-animation-enter {
		opacity: 0;
		transform: translateY(
			${props =>
				props.hasStickyTitle ? '0' : props.moveForward ? '2vh' : '-2vh'}
		);
	}
	&.fields-animation-enter-active,
	&.fields-animation-enter-done {
		opacity: 1;
		transform: translateY(0);
	}

	&.fields-animation-exit {
		opacity: 1;
		transform: translateY(0);
	}
	&.fields-animation-exit-active,
	&.fields-animation-exit-done {
		opacity: 0;
		transform: translateY(
			${props =>
				props.hasStickyTitle
					? '0'
					: props.moveForward
					? '-10vh'
					: '25vh'}
		);
	}

	&.no-animation {
		opacity: 1;
	}
`;
interface ScreenProps {
	stickyTitle?: JSX.Element | string;
	title: JSX.Element;
	fields: JSX.Element;
	key: number;
	avoidTrackFormStepLoad?: boolean;
	thankYouPage?: boolean;
}

export function FullScreen(props: ScreenProps) {
	const {
		stickyTitle,
		title,
		fields,
		key,
		avoidTrackFormStepLoad,
		thankYouPage,
	} = props;
	const { formInfo, formData } = useSelector((state: OneFormState) => state);
	const {
		multiStepScreenIndex,
		screenIndex,
		triggerExitAnimation,
		showFinalScreen,
		numberOfScreens,
		formEnded,
		alingItemsCenter,
		deleteCloseAndPrevious,
		formType,
	} = formInfo;
	const trackingManager = useTrackingManager();
	const {
		distanceToMove,
		setDistanceToMove,
		moveForward,
		nextScreenIndex,
		nextMultiStepScreenIndex,
		showPreviousStep,
	} = useOneFormContext();

	const [linkBasesLegales, setLinkBasesLegales] = useState<string>(null);
	const [isConfirmacionPage, setIsConfirmacionPage] = useState(false);

	useEffect(() => {
		// Añadir link de la promocion a los legales
		if (formData.fields.formName === 'adhoc-2021-eurocopa') {
			setLinkBasesLegales(
				'https://crm.volkswagen.es/cms/2021/legal_concurso_eurocopa_2021.pdf',
			);
		}

		if (showFinalScreen) {
			setIsConfirmacionPage(false);
		}

		if (screenIndex === numberOfScreens - 1) {
			if (
				formData.fields.formName === 'cem-venta' ||
				formData.fields.formName === 'cem-posventa'
			) {
				setIsConfirmacionPage(false);
			} else {
				setIsConfirmacionPage(true);
			}
		} else {
			setIsConfirmacionPage(false);
		}
	}, [screenIndex, showFinalScreen]);

	// calc height stuff
	const [initialHeight, setInitialHeight] = useState(null);
	const [isTrigger, setIsTrigger] = useState<boolean>(false);
	const viewportHeight = useWindowHeight();
	const viewportWidth = useWindowWidth();
	const [isMobile, setIsMobile] = useState(viewportWidth < 960);

	const content = useRef(null);
	const prevStep = useRef(null);

	const [currentRef, setCurrentRef] = useState(null); // optimizar ocn useMemo

	// animation stuff
	const [showFields, setShowFields] = useState(false);
	const [showTitle, setShowTitle] = useState(false);

	// variables para animacion entre steps
	const [distanceToTop, setDistanceToTop] = useState<number>(0);
	const titleRef = useRef<any>(null);
	const contentRef = useRef<any>(null);

	// navigation stuff
	const [formClicked, setFormClicked] = useState(false);

	useEffect(() => {
		if (!formClicked) {
			document.body.scrollTop = 0; // For Safari
			document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
		}
	}, [formClicked]);

	useEffect(() => {
		return;
		setTimeout(() => {
			if (titleRef.current && contentRef.current) {
				const titleLength = titleRef.current.innerText.length;
				// titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });

				// Extra height en breakpoints grandes y dependiendo de la longitud del título, que afecta a la animación desconozco por qué ocurre, pero no depende del viewport height, sino del viewport width
				if (
					window.matchMedia('(min-width: 1600px)').matches &&
					contentRef
				) {
					const extraHeight =
						titleLength < 60 ? (titleLength < 53 ? 8 : 18) : 36;
					setDistanceToTop(titleRef.current.offsetTop + extraHeight);
				} else {
					const extraHeight = titleLength < 53 ? 18 : 0;
					// setDistanceToTop(titleRef.current.getBoundingClientRect().top - extraHeight);
					setDistanceToTop(
						titleRef.current.getBoundingClientRect().top -
							contentRef.current.getBoundingClientRect().top +
							32 -
							extraHeight,
					);
				}
				// Guardar distancia del título a la parte superior del contenedor padre en un array de distancias para saber la distancia que tiene que recorrer el previousStep si la navegación va para atrás
				if (
					(screenIndex === 0 && nextScreenIndex === null) ||
					screenIndex + 1 > distanceToMove.length
				) {
					const extraHeight = titleLength < 53 ? 18 : 0;
					setDistanceToMove(distanceToMove => [
						...distanceToMove,
						titleRef.current.getBoundingClientRect().top -
							contentRef.current.getBoundingClientRect().top +
							32 -
							extraHeight,
					]);
				} else if (
					screenIndex + 1 <= distanceToMove.length &&
					nextMultiStepScreenIndex !== 1 &&
					nextMultiStepScreenIndex !== 2
				) {
					const arrDistances = distanceToMove;
					const extraHeight = titleLength < 53 ? 18 : 0;
					arrDistances[screenIndex] =
						titleRef.current.getBoundingClientRect().top -
						contentRef.current.getBoundingClientRect().top +
						32 -
						extraHeight;
					setDistanceToMove(arrDistances);
				}
			}
		});
	}, [titleRef]);

	useEffect(() => {
		if (triggerExitAnimation) {
			if (!formEnded) {
				setShowTitle(false);
				setShowFields(false);
			}
		}
	}, [triggerExitAnimation]);

	useEffect(() => {
		// Ni en la primera carga ni en la thank you page se tiene que disparar este evento. El flag isFirstStep solo lo necesitamos en la primera carga, después si que se tiene que disparar el evento.

		if (
			formInfo.step?.isFirstStep ||
			avoidTrackFormStepLoad ||
			(formInfo.stepsHistory?.length === 1 &&
				(!multiStepScreenIndex || multiStepScreenIndex === 0))
		) {
			if (formInfo.step) formInfo.step.isFirstStep = false;
			return;
		}
		trackingManager.trackFormStepLoad(
			{
				// contentId: step.title
			},
			{
				FormStart: false,
			},
		);
	}, [title]);

	useEffect(() => {
		if (formType == 'module' && screenIndex > 0 && contentRef?.current) {
			contentRef.current.scrollIntoView({
				behavior: 'instant',
				block: 'center',
				inline: 'nearest',
			});
		}

		if (window.matchMedia('(max-width: 960px)').matches && contentRef) {
			contentRef.current.scrollTo({ top: 0 });
		}
		setShowTitle(true);
	}, []);

	// Que en la zona del mapa no vuelva a lanzar las animaciones
	const [stopAnimation, setStopAnimation] = useState(false);

	useEffect(() => {
		if (
			(formData.fields.formName === 'no_compra-no_tengo' &&
				screenIndex === 4 &&
				multiStepScreenIndex === 0) ||
			(formData.fields.formName === 'no_compra-no_tengo' &&
				screenIndex === 5 &&
				multiStepScreenIndex === 0)
		) {
			setStopAnimation(true);
		}
	}, [screenIndex]);

	const [hideCloseAndPrevious, setHideCloseAndPrevious] = useState(false);

	useEffect(() => {
		if (deleteCloseAndPrevious) {
			if (
				(formData.fields.formName === 'lead-test_drive-configure' &&
					multiStepScreenIndex === 0 &&
					screenIndex === 0) ||
				(formData.fields.formName === 'lead-presupuesto-configure' &&
					multiStepScreenIndex === 0 &&
					screenIndex === 0) ||
				(formData.fields.formName ===
					'lead-mas_informacion-configure' &&
					multiStepScreenIndex === 0 &&
					screenIndex === 0) ||
				(formData.fields.formName === 'lead-cita-configure' &&
					multiStepScreenIndex === 0 &&
					screenIndex === 0)
			) {
				setHideCloseAndPrevious(true);
			} else {
				setHideCloseAndPrevious(false);
			}
		} else {
			setHideCloseAndPrevious(false);
		}
	}, [screenIndex]);

	const [alingContentCenter, setAlingContentCenter] = useState(false);

	useEffect(() => {
		setIsMobile(viewportWidth < 960);
		if (viewportWidth < 960 || alingItemsCenter === true) {
			setAlingContentCenter(true);
		} else {
			setAlingContentCenter(false);
		}
	}, [screenIndex, viewportWidth]);

	return (
		<>
			<FullScreenWrapper
				className="full-screen"
				ref={contentRef}
				isTrigger={isTrigger}
			>
				<div style={{ width: '100%' }}>
					<Layout
						allowOverflowingContent
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 2 },
								{ name: 'a', columns: 12 },
								{ name: 'b', columns: 8 },
								{ name: '.', columns: 2 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 3 },
								{ name: 'a', columns: 14 },
								{ name: 'b', columns: 4 },
								{ name: '.', columns: 3 },
							],
							[Breakpoints.b1280]: [
								{ name: '.', columns: 5 },
								{ name: 'a', columns: 10 },
								{ name: 'b', columns: 4 },
								{ name: '.', columns: 5 },
							],
							[Breakpoints.b1920]: [
								{ name: '.', columns: 6 },
								{ name: 'a', columns: 8 },
								{ name: 'b', columns: 4 },
								{ name: '.', columns: 6 },
							],
							[Breakpoints.b2560]: [
								{ name: '.', columns: 7 },
								{ name: 'a', columns: 6 },
								{ name: 'b', columns: 4 },
								{ name: '.', columns: 7 },
							],
						}}
					>
						{showFinalScreen ||
						hideCloseAndPrevious ||
						thankYouPage ? null : !isMobile && !showPreviousStep ? (
							<div>&nbsp;</div>
						) : !isMobile ? (
							<CSSTransition
								classNames="prevstep-animation"
								timeout={10}
								in={showTitle}
							>
								<PreviousStepAnimationWrapper
									className={
										stopAnimation ? 'no-animation' : ''
									}
									moveForward={moveForward}
									showPreviousStep={
										showPreviousStep && screenIndex > 1
									}
									ref={prevStep}
								>
									<PreviousStep
										notALayer={true}
										stopAnimation={stopAnimation}
									/>
								</PreviousStepAnimationWrapper>
							</CSSTransition>
						) : (
							<div>&nbsp;</div>
						)}
						{showFinalScreen ||
						hideCloseAndPrevious ||
						thankYouPage ? null : screenIndex === 0 ? (
							<div>&nbsp;</div>
						) : (
							<div style={{ textAlign: 'right' }}>
								<AnteriorCTA />
							</div>
						)}
					</Layout>
				</div>

				<ContentWrapper ref={content} className="content-wrapper">
					<Layout
						allowOverflowingContent
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 2 },
								{ name: 'a', columns: 20 },
								{ name: '.', columns: 2 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 3 },
								{ name: 'a', columns: 18 },
								{ name: '.', columns: 3 },
							],
							[Breakpoints.b1280]: [
								{ name: '.', columns: 5 },
								{ name: 'a', columns: 14 },
								{ name: '.', columns: 5 },
							],
							[Breakpoints.b1920]: [
								{ name: '.', columns: 6 },
								{ name: 'a', columns: 12 },
								{ name: '.', columns: 6 },
							],
							[Breakpoints.b2560]: [
								{ name: '.', columns: 7 },
								{ name: 'a', columns: 10 },
								{ name: '.', columns: 7 },
							],
						}}
					>
						<>
							{showFinalScreen ? (
								<Container
									padding={{
										bottom: ContainerPadding.dynamic0050,
									}}
								>
									<AnimatedCheckmark className="animated-checkmark">
										<Checkmark variant="default" />
									</AnimatedCheckmark>
								</Container>
							) : null}

							{stickyTitle ? (
								<Text
									appearance={TokenTextAppearance.headline300}
								>
									{stickyTitle}
								</Text>
							) : null}

							<CSSTransition
								classNames="title-animation"
								timeout={10}
								in={showTitle}
								key={key}
								onEntered={() => {
									setShowFields(true);
								}}
							>
								<TitleAnimationWrapper
									className={
										stopAnimation ? 'no-animation' : ''
									}
									moveForward={moveForward}
									hasStickyTitle={!!stickyTitle}
									ref={titleRef}
								>
									<Text
										appearance={
											TokenTextAppearance.headline300
										}
									>
										{title}
									</Text>
								</TitleAnimationWrapper>
							</CSSTransition>
						</>
					</Layout>

					<CSSTransition
						classNames="fields-animation"
						timeout={10}
						in={showFields}
						key={key}
					>
						<FieldsAnimationWrapper
							moveForward={moveForward}
							hasStickyTitle={!!stickyTitle}
						>
							{fields}
						</FieldsAnimationWrapper>
					</CSSTransition>
				</ContentWrapper>
			</FullScreenWrapper>
			{showFinalScreen ? null : (
				<>
					{isConfirmacionPage ? (
						<Layout
							allowOverflowingContent
							appearance={{
								[Breakpoints.default]: [
									{ name: '.', columns: 2 },
									{ name: 'a', columns: 20 },
									{ name: '.', columns: 2 },
								],
							}}
						>
							<Legales linkBasesLegales={linkBasesLegales} />
						</Layout>
					) : null}
				</>
			)}
		</>
	);
}
